import React, { useEffect, useState } from 'react';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import {
  DynamicFormInputType,
  IDynamicSection,
  IFormDateDynamicProps,
  IFormSelectDynamicProps,
} from '@components/_old/dynamicForm';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { inputs } from './content';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import Loader from '@components/loader/Loader';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IPolicyDetailsDrawerProps } from '.';
import SectionDynamicForm from '@components/_old/dynamicForm/SectionDynamicForm';
import { getListForm, getModelsByBrands, updatePolicy } from './queries';
import { isPublicUsageType, toLookups } from './utils';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import { normaliseDynamicValues } from '@utils/dynamic-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useAppSelector } from '@redux/hooks';
import DateService from '@services/DateService';

const PolicyDetailsDrawer: React.FC<IPolicyDetailsDrawerProps> = ({
  policyId,
  open,
  onSuccess,
  onClose,
  policyDetailsInfo,
}) => {
  const user = useAppSelector((state) => state.user);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] =
    useState<Record<string, DynamicFormInputType>>(inputs);

  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({
    model: false,
  });

  const policyDetailsListResults = useQuery(getListForm());

  const [getModelsByBrandsLazy, modelByBrandsQueryRes] =
    useLazyQuery(getModelsByBrands());

  const [policyAction] = useMutation(updatePolicy());

  const sections: Record<string, IDynamicSection> = {
    section1: {
      title: 'Policy Detail',
      inputs: {
        policyNumber: inputsForm.policyNumber,
        isRenewal: inputsForm.isRenewal,
        renewalNumber: inputsForm.renewalNumber,
        policyEffectiveDate: inputsForm.policyEffectiveDate,
        policyIssueDate: inputsForm.policyIssueDate,
        policyExpiryDate: inputsForm.policyExpiryDate,
        product: inputsForm.product,
        towingService: inputsForm.towingService,
        vignetteCode: inputsForm.vignetteCode,
        vignette: inputsForm.vignette,
      },
    },
    section2: {
      title: 'Customer Details',
      inputs: {
        nameOnLicenseEn: inputsForm.nameOnLicenseEn,
        nameOnLicenseAr: inputsForm.nameOnLicenseAr,
        firstName: inputsForm.firstName,
        middleName: inputsForm.middleName,
        lastName: inputsForm.lastName,
        email: inputsForm.email,
        phoneType: inputsForm.phoneType,
        mobileNumber: inputsForm.mobileNumber,
        country: inputsForm.country,
        city: inputsForm.city,
        street: inputsForm.street,
        building: inputsForm.building,
      },
    },
    section3: {
      title: 'Vehicle Details',
      inputs: {
        plateCode: inputsForm.plateCode,
        plateNumber: inputsForm.plateNumber,
        usageType: inputsForm.usageType,
        bodyType: inputsForm.bodyType,
        engineType: inputsForm.engineType,
        brand: inputsForm.brand,
        model: inputsForm.model,
        yearOfMake: inputsForm.yearOfMake,
        motor: inputsForm.motor,
        weight: inputsForm.weight,
        engineSize: inputsForm.engineSize,
        seats: inputsForm.seats,
        chassis: inputsForm.chassis,
        horsePower: inputsForm.horsePower,
      },
    },
  };

  const userAgentType = user.info.agentType;

  useEffect(() => {
    initialize();
  }, [policyDetailsListResults]);

  useEffect(() => {
    if (modelByBrandsQueryRes.loading) {
      setLoadingFields({ ...loadingFields, model: true });
    }
    if (modelByBrandsQueryRes.error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
    if (modelByBrandsQueryRes.data) {
      const models = lookupListAsRecordObject(
        modelByBrandsQueryRes.data?.Insurance?.lookups?.models
      );

      setInputsForm((oldInputForms) => {
        const newInputForm = cloneDeep(oldInputForms);
        (newInputForm.model as IFormSelectDynamicProps).selectOptions = models;
        return newInputForm;
      });
      setLoadingFields({ ...loadingFields, model: false });
    }
  }, [modelByBrandsQueryRes]);

  const initialize = () => {
    try {
      const updatedInputs = cloneDeep(inputsForm);

      // ---- Lookups ----
      if (policyDetailsListResults.data) {
        const usageType = isPublicUsageType(
          policyDetailsInfo?.usageTypeCode,
          policyDetailsInfo?.usageType
        )
          ? 'Public'
          : '';
        const lookupLists = toLookups(policyDetailsListResults.data, usageType);

        (updatedInputs.phoneType as IFormSelectDynamicProps).selectOptions =
          lookupLists['phoneType'];

        (updatedInputs.isRenewal as IFormSelectDynamicProps).selectOptions =
          lookupLists['renewalStatus'];

        (updatedInputs.country as IFormSelectDynamicProps).selectOptions =
          lookupLists['countries'];

        (updatedInputs.brand as IFormSelectDynamicProps).selectOptions =
          lookupLists['brands'];

        (updatedInputs.plateCode as IFormSelectDynamicProps).selectOptions =
          lookupLists['plateCode'];

        (updatedInputs.engineType as IFormSelectDynamicProps).selectOptions =
          lookupLists['engineTypes'];
      }

      const agentType = (userAgentType ?? '').trim().toUpperCase();
      const isExternal = agentType === 'EXTERNAL';

      // Auto expiry (+1y)
      (updatedInputs.policyEffectiveDate as IFormDateDynamicProps).onChange = (
        event
      ) => {
        setInputsForm((current) => ({
          ...current,
          policyExpiryDate: {
            ...current.policyExpiryDate,
            value: DateService.addYears(new Date(event), 1).toISOString(),
          },
        }));
      };

      // ---- Populate from API ----
      if (policyDetailsInfo) {
        updatedInputs.policyNumber.value = policyDetailsInfo.policyNumber;
        updatedInputs.isRenewal.value = policyDetailsInfo.isRenewal;
        updatedInputs.renewalNumber.value = policyDetailsInfo.renewalNumber;
        updatedInputs.policyEffectiveDate.value =
          policyDetailsInfo.policyEffectiveDate;
        updatedInputs.policyIssueDate.value = policyDetailsInfo.policyIssueDate;
        updatedInputs.policyExpiryDate.value =
          policyDetailsInfo.policyExpiryDate;
        updatedInputs.product.value = policyDetailsInfo.plan;
        updatedInputs.productCode.value = policyDetailsInfo.productCode;
        updatedInputs.towingService.value = policyDetailsInfo.towingService;
        updatedInputs.vignetteCode.value = policyDetailsInfo.vignetteCode;
        updatedInputs.vignette.value = policyDetailsInfo.vignette;

        updatedInputs.nameOnLicenseEn.value = policyDetailsInfo.nameOnLicenseEn;
        updatedInputs.nameOnLicenseAr.value = policyDetailsInfo.nameOnLicenseAr;
        updatedInputs.firstName.value = policyDetailsInfo.firstName;
        updatedInputs.middleName.value = policyDetailsInfo.middleName;
        updatedInputs.lastName.value = policyDetailsInfo.lastName;
        updatedInputs.email.value = policyDetailsInfo.email;
        updatedInputs.phoneType.value = policyDetailsInfo.phoneType;
        updatedInputs.mobileNumber.value = policyDetailsInfo.mobileNumber;
        updatedInputs.country.value = policyDetailsInfo.countryId;
        updatedInputs.city.value = policyDetailsInfo.city;
        updatedInputs.street.value = policyDetailsInfo.street;
        updatedInputs.building.value = policyDetailsInfo.building;

        const isPublicUsage = isPublicUsageType(
          policyDetailsInfo.usageTypeCode,
          policyDetailsInfo.usageType
        );
        const isPlateCodeP = [
          policyDetailsInfo.plateCodeCode,
          policyDetailsInfo.plateCode,
        ].some((value) => (value || '').trim().toUpperCase() === 'P');
        updatedInputs.plateCode.value =
          !isPublicUsage && isPlateCodeP ? '' : policyDetailsInfo.plateCode;
        updatedInputs.plateNumber.value = policyDetailsInfo.plateNumber;
        updatedInputs.usageType.value = policyDetailsInfo.usageType;
        updatedInputs.bodyType.value = policyDetailsInfo.bodyType;
        updatedInputs.engineType.value = policyDetailsInfo.engineTypeCode;
        updatedInputs.brand.value = policyDetailsInfo.brandId;
        updatedInputs.model.value = policyDetailsInfo.modelId;
        updatedInputs.yearOfMake.value = policyDetailsInfo.yearOfMake;
        updatedInputs.motor.value = policyDetailsInfo.motor;
        updatedInputs.weight.value = policyDetailsInfo.weight;
        updatedInputs.engineSize.value = policyDetailsInfo.engineSize;
        updatedInputs.seats.value = policyDetailsInfo.seats;
        updatedInputs.chassis.value = policyDetailsInfo.chassis;
        updatedInputs.horsePower.value = policyDetailsInfo.horsePower;
      }

      if (updatedInputs.isRenewal.value == 'YES') {
        updatedInputs.renewalNumber.hidden = false;
        updatedInputs.renewalNumber.required = true;
      } else {
        updatedInputs.renewalNumber.hidden = true;
      }

      updatedInputs.seats.disabled = ![
        '4x4',
        'CRANE',
        'F_TANKER',
        'MOTORCYCLE',
        'PICKUP',
        'SEDAN',
        'STATION',
        'SUV',
        'TAXI',
        'TOWING',
        'TRUCK',
        'VAN',
      ].includes(policyDetailsInfo?.bodyType?.toString());

      if (updatedInputs.brand.value) {
        getModelsByBrandsLazy({
          variables: { modelId: updatedInputs.brand.value },
        });
      }

      if (updatedInputs.productCode.value == 'BodilyInjury') {
        updatedInputs.vignetteCode.hidden = false;
        updatedInputs.vignetteCode.required = true;
        updatedInputs.vignette.hidden = false;
        updatedInputs.vignette.required = true;
      } else {
        updatedInputs.vignetteCode.hidden = true;
        updatedInputs.vignette.hidden = true;
      }

      // ---- LOVs (PolicyEffectiveDate) per AC ----
      {
        const effInput =
          updatedInputs.policyEffectiveDate as IFormDateDynamicProps;
        const issueDateSrc =
          policyDetailsInfo?.policyIssueDate ??
          (updatedInputs.policyIssueDate as IFormDateDynamicProps)?.value ??
          null;

        if (issueDateSrc) {
          const issue = new Date(issueDateSrc);
          if (!isNaN(issue.getTime())) {
            const yearStart = new Date(issue.getFullYear(), 0, 1);
            const yearEnd = new Date(
              issue.getFullYear(),
              11,
              31,
              23,
              59,
              59,
              999
            );

            const now = new Date();
            const todayStart = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate()
            );

            // Base ranges
            const baseMin = isExternal
              ? issue
              : DateService.subtractMonths(issue, 3);
            const baseMax = DateService.addMonths(issue, 1);

            // Final ranges (floored at Today)
            const rawMin = isExternal
              ? todayStart
              : DateService.subtractMonths(todayStart, 3);
            const rawMax = baseMax;

            const finalMin = rawMin < yearStart ? yearStart : rawMin;
            const finalMax = rawMax > yearEnd ? yearEnd : rawMax;

            effInput.minDate = finalMin > finalMax ? undefined : finalMin;
            effInput.maxDate = finalMin > finalMax ? undefined : finalMax;
          }
        }
      }

      // ---- Disable unless Today ∈ [Issue, Issue+1M] ----
      {
        const effInput =
          updatedInputs.policyEffectiveDate as IFormDateDynamicProps;

        const issueDateSrc =
          policyDetailsInfo?.policyIssueDate ??
          (updatedInputs.policyIssueDate?.value as any) ??
          null;

        const issueDate = issueDateSrc ? new Date(issueDateSrc) : undefined;

        if (!issueDate || isNaN(issueDate.getTime())) {
          effInput.disabled = true;
        } else {
          const windowEnd = DateService.addMonths(issueDate, 1);
          const start = new Date(
            issueDate.getFullYear(),
            issueDate.getMonth(),
            issueDate.getDate()
          );
          const end = new Date(
            windowEnd.getFullYear(),
            windowEnd.getMonth(),
            windowEnd.getDate(),
            23,
            59,
            59,
            999
          );
          const today = new Date();

          effInput.disabled = !(today >= start && today <= end);
        }
      }

      setInputsForm(updatedInputs);
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normaliseDynamicValues(inputs, values);

    if (data.isRenewal == 'NO') {
      data.renewalNumber = null;
    }
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variablesMutation = {
        entityId: policyId,
        country: data.country,
        city: data.city,
        street: data.street || null,
        building: data.building || null,
        mobileNumber: data.mobileNumber,
        isRenewal: data.isRenewal,
        renewalNumber: data.renewalNumber,
        brand: data.brand || null,
        model: data.brand ? data.model : null,
        chassis: data.chassis,
        motor: data.motor,
        nameOnLicenseAr: data.nameOnLicenseAr,
        nameOnLicenseEn: data.nameOnLicenseEn,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        horsePower: parseInt(data.horsePower as unknown as string),
        phoneType: data.phoneType,
        plateCode: !isEmpty(data.plateCode) ? data.plateCode : undefined,
        plateNumber: !isEmpty(data.plateNumber)
          ? `${data.plateNumber}`
          : undefined,
        vignetteCode: !isEmpty(data.vignetteCode)
          ? data.vignetteCode
          : undefined,
        seats: data.seats ? Number(data.seats) : undefined,
        vignette: data.vignette ? Number(data.vignette) : undefined,
        effectiveDate: DateService.formatDateBackend(data.policyEffectiveDate),
        yearOfMake: parseInt(data.yearOfMake as any),
        engineType: data.engineType,
      };

      policyAction({
        variables: variablesMutation,
        errorPolicy: 'all',
      }).then((res) => {
        if (isEmpty(res.errors)) {
          toast.success(
            <ToastSuccessMessage>
              {'Policy Updated Successfully'}
            </ToastSuccessMessage>
          );
          setTimeout(() => {
            setSubmitButtonState('success');
            onSuccess();
            onClose();
          }, 500);
        } else {
          setSubmitButtonState(undefined);
          toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        }
      });
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <GenericDrawer
      title="Edit Policy Details"
      onClose={() => onClose()}
      isOpen={open}
    >
      {policyDetailsListResults.loading && open ? (
        <Loader />
      ) : (
        <>
          <SectionDynamicForm
            onSubmit={(values) => submitForm(values)}
            buttonText={'Submit'}
            submitButtonState={submitButtonState}
            disableForm={formDisabled}
            inputs={inputsForm}
            sections={sections}
            loadingFields={loadingFields}
            onChange={(
              fieldName: string,
              value: string | string[],
              allValues
            ) => {
              const formInputs = cloneDeep(inputsForm);

              if (fieldName === 'isRenewal') {
                if (value == 'YES') {
                  formInputs.renewalNumber.required = true;
                  formInputs.renewalNumber.hidden = false;
                } else {
                  formInputs.renewalNumber.hidden = true;
                }

                Object.values(formInputs).forEach((input) => {
                  input.value = allValues[input.name];
                });

                setInputsForm({ ...formInputs });
              }

              if (fieldName === 'brand') {
                getModelsByBrandsLazy({
                  variables: { modelId: value as string },
                });

                Object.values(formInputs).forEach((input) => {
                  input.value = allValues[input.name];
                });

                setInputsForm({ ...formInputs });
              }
            }}
          />
        </>
      )}
    </GenericDrawer>
  );
};

export default PolicyDetailsDrawer;
